// A typed client over a delve-sim worker: requests matched to replies by id, one at a time in the worker. `cancel`
// ends the worker (a long `validate` cannot be interrupted otherwise); the next call starts a fresh one, which must
// load its export again (`ensureLoaded`).
import type { Cmd, Commands, SimReply, SimRequest } from "./protocol";

export class SimError extends Error {}

export interface WorkerLike {
  postMessage(message: SimRequest): void;
  terminate(): void;
  onmessage: ((e: MessageEvent<SimReply>) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
}

export type WorkerFactory = () => WorkerLike;

export const defaultWorker: WorkerFactory = () =>
  new Worker(new URL("./sim.worker.ts", import.meta.url), { type: "module" }) as unknown as WorkerLike;

export class SimClient {
  private worker: WorkerLike | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve(v: unknown): void; reject(e: Error): void }>();
  private loaded: { key: string; json: string | object } | null = null;
  private loadedIn: WorkerLike | null = null;

  constructor(private readonly factory: WorkerFactory = defaultWorker) {}

  private get live(): WorkerLike {
    if (this.worker) return this.worker;
    const w = this.factory();
    w.onmessage = ({ data }) => {
      const waiting = this.pending.get(data.id);
      if (!waiting) return;
      this.pending.delete(data.id);
      if (data.ok) waiting.resolve(data.result);
      else waiting.reject(new SimError(data.error));
    };
    w.onerror = (e) => this.fail(new SimError(e.message || "delve-sim stopped"));
    this.worker = w;
    return w;
  }

  private fail(error: Error): void {
    for (const p of this.pending.values()) p.reject(error);
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
  }

  private send<T>(cmd: string, args: unknown): Promise<T> {
    const id = this.nextId++;
    const worker = this.live;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      worker.postMessage({ id, cmd, args });
    });
  }

  /** Loads an export (biomes.json) once per worker; `key` names it (the pack), so the same export is not sent twice. */
  async ensureLoaded(key: string, json: string | object): Promise<void> {
    const worker = this.live;
    if (this.loaded?.key === key && this.loadedIn === worker) return;
    await this.send("load", { json, name: key });
    this.loaded = { key, json };
    this.loadedIn = worker;
  }

  async request<C extends Exclude<Cmd, "load">>(cmd: C, args: Commands[C]["args"]): Promise<Commands[C]["result"]> {
    if (this.loaded && this.loadedIn !== this.live) await this.ensureLoaded(this.loaded.key, this.loaded.json);
    return this.send<Commands[C]["result"]>(cmd, args);
  }

  /** Stops what the worker is doing (its pending calls fail with `SimError("cancelled")`). */
  cancel(): void {
    this.fail(new SimError("cancelled"));
  }

  dispose(): void {
    this.fail(new SimError("closed"));
  }
}
